import { useEffect, useState } from "react";
import { Loader2, RotateCcw, Trophy, Brain, Disc3, Coins, Store, Target, Puzzle, PartyPopper, Plus, Trash2, Dices } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";

const GOLD_TOAST = { background: "#1A1A1A", color: "#D4AF37", border: "1px solid rgba(212,175,55,0.4)" };
const KINDS = [
  { v: "mela", label: "REGAL COINS" },
  { v: "code", label: "COUNTER CODE" },
  { v: "again", label: "TRY AGAIN" },
];
const GAMES = [
  { key: "chakkar", name: "Mela Da Chakkar", sub: "prize wheel" },
  { key: "trivia", name: "Punjab Flavor Pehchaan", sub: "trivia + Ustaad" },
  { key: "scratch", name: "Sunehri Patti", sub: "scratch cards" },
  { key: "runner", name: "Pind Food Run", sub: "endless runner" },
  { key: "masala", name: "Masala Match", sub: "spice pairing" },
  { key: "ringtoss", name: "Choori Da Challa", sub: "ring toss" },
  { key: "saap", name: "Saap-Seedi", sub: "family board game" },
  { key: "kanche", name: "Kanche Champion", sub: "Marble circle, 5 flicks" },
  { key: "cricket", name: "Urban vs Regal — Cricket Clash", sub: "Six-ball final over chase" },
  { key: "ludo", name: "Kaudi Chaupar", sub: "Shell-throw board, 2-4 players" },
  { key: "carrom", name: "Carrom Chaupal", sub: "striker physics" },
];

const numOr = (v, fb) => { const n = parseInt(v, 10); return Number.isNaN(n) ? fb : n; };

const Num = ({ label, testid, value, set, w = "w-24" }) => (
  <label className="block mt-3">
    <span className="block text-[10px] text-white/50 font-bold">{label}</span>
    <input data-testid={testid} value={value} inputMode="numeric"
      onChange={(e) => set(e.target.value.replace(/\D/g, ""))}
      className={`mt-1 ${w} bg-[#1A1A1A] border border-white/10 rounded-full px-3 py-2 text-sm text-center focus:outline-none focus:border-[#D4AF37]/60`} />
  </label>
);

const MelaEvents = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/mela-events").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const { events, active_id } = data;
  const patch = (i, p) => setData((d) => ({ ...d, events: d.events.map((e, j) => (j === i ? { ...e, ...p } : e)) }));
  const today = new Date().toISOString().slice(0, 10);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/mela-events", {
        events: events.map((e) => ({ ...e, multiplier: numOr(e.multiplier, 1) })) });
      setData(r.data);
      toast.success("Seasonal mela nights saved", { style: GOLD_TOAST });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save events"); }
    setSaving(false);
  };

  return (
    <div className="rounded-xl bg-[#121212] border border-[#D4AF37]/25 p-4" data-testid="admin-mela-events">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-[10px] tracking-widest text-[#D4AF37] font-bold flex items-center gap-1.5"><PartyPopper size={12} /> SEASONAL MELA NIGHTS</p>
          <p className="text-[10px] text-white/40 mt-1">Baisakhi, Lohri… during the dates a festive banner shows in the mela and every Regal Coins win is multiplied. Urban Points are never involved — games pay fun coins only.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="mela-event-add" onClick={() => setData((d) => ({ ...d, events: [...d.events, { id: "", name: "", start: today, end: today, multiplier: 2, message: "" }] }))}
            className="flex items-center gap-1.5 text-[10px] font-bold rounded-full px-4 py-2.5 tracking-widest border border-white/20 text-white/60">
            <Plus size={11} /> ADD EVENT
          </button>
          <button data-testid="mela-events-save" onClick={save} disabled={saving}
            className="bg-[#D4AF37] text-black text-[10px] font-black rounded-full px-5 py-2.5 tracking-widest disabled:opacity-50">
            {saving ? <Loader2 size={11} className="animate-spin" /> : "SAVE EVENTS"}
          </button>
        </div>
      </div>
      {events.length === 0 ? (
        <p className="text-xs text-white/35 mt-3">No events yet — add Baisakhi Mela or Lohri Night above.</p>
      ) : (
        <div className="mt-3 space-y-2">
          {events.map((e, i) => (
            <div key={i} data-testid={`mela-event-row-${i}`} className="flex flex-wrap items-center gap-2 rounded-lg bg-[#1A1A1A] border border-white/5 p-2">
              <input data-testid={`mela-event-name-${i}`} value={e.name} maxLength={40} placeholder="e.g. Baisakhi Mela"
                onChange={(ev) => patch(i, { name: ev.target.value })}
                className="flex-1 min-w-[120px] bg-[#121212] border border-white/10 rounded-full px-3 py-2 text-xs focus:outline-none focus:border-[#D4AF37]/60" />
              <input data-testid={`mela-event-start-${i}`} type="date" value={e.start}
                onChange={(ev) => patch(i, { start: ev.target.value })}
                className="bg-[#121212] border border-white/10 rounded-full px-3 py-1.5 text-xs text-white/70 focus:outline-none" />
              <span className="text-[9px] text-white/30 font-bold">TO</span>
              <input data-testid={`mela-event-end-${i}`} type="date" value={e.end}
                onChange={(ev) => patch(i, { end: ev.target.value })}
                className="bg-[#121212] border border-white/10 rounded-full px-3 py-1.5 text-xs text-white/70 focus:outline-none" />
              <select data-testid={`mela-event-mult-${i}`} value={e.multiplier}
                onChange={(ev) => patch(i, { multiplier: parseInt(ev.target.value, 10) })}
                className="bg-[#121212] border border-white/10 rounded-full px-2.5 py-2 text-[10px] font-bold text-white/70 focus:outline-none">
                {[1, 2, 3, 4, 5].map((m) => <option key={m} value={m}>{m}x REGAL COINS</option>)}
              </select>
              <input data-testid={`mela-event-msg-${i}`} value={e.message} maxLength={80} placeholder="banner message (optional)"
                onChange={(ev) => patch(i, { message: ev.target.value })}
                className="flex-1 min-w-[140px] bg-[#121212] border border-white/10 rounded-full px-3 py-2 text-xs focus:outline-none focus:border-[#D4AF37]/60" />
              {e.id && e.id === active_id && (
                <span data-testid={`mela-event-active-${i}`} className="text-[8px] font-black tracking-widest bg-[#E25822] text-white rounded-full px-2.5 py-1">ACTIVE NOW</span>
              )}
              <button data-testid={`mela-event-del-${i}`} aria-label="Delete event"
                onClick={() => setData((d) => ({ ...d, events: d.events.filter((_, j) => j !== i) }))}
                className="w-8 h-8 rounded-full border border-[#E25822]/40 text-[#E25822] flex items-center justify-center"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const AdminArcade = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    api.get("/admin/arcade-config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const patchSeg = (i, patch) =>
    setCfg((p) => ({ ...p, spin_segments: p.spin_segments.map((s, j) => (j === i ? { ...s, ...patch } : s)) }));
  const field = (k) => (v) => setCfg((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/arcade-config", {
        spin_segments: cfg.spin_segments.map((s) => ({
          ...s, w: numOr(s.w, 1),
          ...(s.kind === "mela" ? { mela: numOr(s.mela, 10) } : {}),
        })),
        games_enabled: cfg.games_enabled,
        trivia_mela_correct: numOr(cfg.trivia_mela_correct, 5),
        trivia_mela_wrong: numOr(cfg.trivia_mela_wrong, 5),
        trivia_streak_bonus: numOr(cfg.trivia_streak_bonus, 25),
        ustaad_mela_per_correct: numOr(cfg.ustaad_mela_per_correct, 20),
        mela_game_daily_cap: numOr(cfg.mela_game_daily_cap, 100),
        runner_mela_first: numOr(cfg.runner_mela_first, 750),
        runner_mela_prizes: cfg.runner_mela_prizes.map((p) => numOr(p, 0)),
        masala_mela_correct: numOr(cfg.masala_mela_correct, 10),
        ringtoss_mela_ring: numOr(cfg.ringtoss_mela_ring, 15),
        kanche_mela_marble: numOr(cfg.kanche_mela_marble, 15),
        cricket_mela_per_run: numOr(cfg.cricket_mela_per_run, 2),
        cricket_mela_win: numOr(cfg.cricket_mela_win, 40),
        saap_mela_win: numOr(cfg.saap_mela_win, 30),
        ludo_mela_win: numOr(cfg.ludo_mela_win, 40),
        carrom_mela_win: numOr(cfg.carrom_mela_win, 40),
      });
      setCfg(r.data);
      toast.success("Mela economy saved — live for guests immediately", { style: GOLD_TOAST });
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setSaving(false);
  };

  const reset = async () => {
    if (!confirmReset) {
      setConfirmReset(true);
      setTimeout(() => setConfirmReset(false), 3000);
      return;
    }
    try {
      const r = await api.put("/admin/arcade-config", { reset: true });
      setCfg(r.data);
      toast.success("Back to the default mela economy", { style: GOLD_TOAST });
    } catch { toast.error("Could not reset"); }
    setConfirmReset(false);
  };

  if (!cfg) return <p className="text-xs text-white/40">Loading…</p>;
  const totalW = cfg.spin_segments.reduce((a, s) => a + (numOr(s.w, 0) || 0), 0);

  return (
    <div className="space-y-5" data-testid="admin-arcade">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-[10px] tracking-widest font-bold text-white/70">PURANA PUNJAB MELA — GAME ECONOMY</p>
          <p className="text-[11px] text-white/45">Regal Coins = fun points for the mela leaderboard & bragging rights. Games never award Urban Points — real rewards come only from orders and passport challenges.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="arcade-config-reset" onClick={reset}
            className={`flex items-center gap-1.5 text-[10px] font-bold rounded-full px-4 py-2.5 tracking-widest border ${confirmReset ? "border-[#E25822] text-[#E25822]" : "border-white/20 text-white/50"}`}>
            <RotateCcw size={11} /> {confirmReset ? "TAP AGAIN TO RESET" : "RESET DEFAULTS"}
          </button>
          <button data-testid="arcade-config-save" onClick={save} disabled={saving}
            className="bg-[#D4AF37] text-black text-[10px] font-black rounded-full px-6 py-2.5 tracking-widest disabled:opacity-50">
            {saving ? <Loader2 size={11} className="animate-spin" /> : "SAVE ALL"}
          </button>
        </div>
      </div>

      {/* STALLS OPEN / CLOSED */}
      <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
        <p className="text-[10px] tracking-widest text-[#D4AF37] font-bold flex items-center gap-1.5"><Store size={12} /> MELA STALLS — OPEN / CLOSED</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-3">
          {GAMES.map((g) => {
            const on = cfg.games_enabled?.[g.key] !== false;
            return (
              <button key={g.key} data-testid={`arcade-game-toggle-${g.key}`}
                onClick={() => setCfg((p) => ({ ...p, games_enabled: { ...p.games_enabled, [g.key]: !on } }))}
                className={`flex items-center justify-between rounded-lg border px-3 py-2.5 text-left ${on ? "border-[#D4AF37]/40 bg-[#1C160A]" : "border-white/10 bg-[#1A1A1A] opacity-60"}`}>
                <span>
                  <span className="block text-[11px] font-black">{g.name}</span>
                  <span className="block text-[9px] text-white/40">{g.sub}</span>
                </span>
                <span className={`text-[9px] font-black tracking-widest rounded-full px-2.5 py-1 ${on ? "bg-[#D4AF37] text-black" : "bg-white/10 text-white/50"}`}>
                  {on ? "OPEN" : "CLOSED"}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* REGAL COINS DAILY LIMIT */}
      <div className="rounded-xl bg-[#121212] border border-[#E25822]/30 p-4">
        <p className="text-[10px] tracking-widest text-[#E25822] font-bold flex items-center gap-1.5"><Coins size={12} /> REGAL COINS — FUN POINTS ONLY (NO URBAN POINTS FROM GAMES)</p>
        <div className="flex flex-wrap gap-x-6">
          <Num label="REGAL COINS / GAME / DAY (10-2000)" testid="arcade-mela-cap" value={cfg.mela_game_daily_cap} set={field("mela_game_daily_cap")} w="w-28" />
        </div>
      </div>

      {/* WHEEL */}
      <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
        <p className="text-[10px] tracking-widest text-[#D4AF37] font-bold flex items-center gap-1.5"><Disc3 size={12} /> MELA DA CHAKKAR — 8 SEGMENTS</p>
        <p className="text-[10px] text-white/40 mt-1">Weight = how often it lands (out of {totalW}). The drawn wheel never shows the odds — big prizes stay rare.</p>
        <div className="mt-3 space-y-2">
          {cfg.spin_segments.map((s, i) => (
            <div key={i} data-testid={`arcade-seg-${i}`} className="flex flex-wrap items-center gap-2 rounded-lg bg-[#1A1A1A] border border-white/5 p-2">
              <span className="text-[10px] font-black text-white/30 w-4">{i + 1}</span>
              <input data-testid={`arcade-seg-label-${i}`} value={s.label} maxLength={22}
                onChange={(e) => patchSeg(i, { label: e.target.value })}
                className="flex-1 min-w-[110px] bg-[#121212] border border-white/10 rounded-full px-3 py-2 text-xs focus:outline-none focus:border-[#D4AF37]/60" />
              <select data-testid={`arcade-seg-kind-${i}`} value={s.kind}
                onChange={(e) => patchSeg(i, { kind: e.target.value })}
                className="bg-[#121212] border border-white/10 rounded-full px-2.5 py-2 text-[10px] font-bold text-white/70 focus:outline-none">
                {KINDS.map((k) => <option key={k.v} value={k.v}>{k.label}</option>)}
              </select>
              {s.kind === "mela" && (
                <input data-testid={`arcade-seg-mela-${i}`} value={s.mela ?? ""} inputMode="numeric" placeholder="rupees"
                  onChange={(e) => patchSeg(i, { mela: e.target.value.replace(/\D/g, "") })}
                  className="w-20 bg-[#121212] border border-white/10 rounded-full px-3 py-2 text-xs text-center focus:outline-none focus:border-[#D4AF37]/60" />
              )}
              {s.kind === "code" && (
                <input data-testid={`arcade-seg-code-${i}`} value={s.code_label ?? ""} maxLength={40} placeholder="what the counter honours"
                  onChange={(e) => patchSeg(i, { code_label: e.target.value })}
                  className="flex-1 min-w-[130px] bg-[#121212] border border-white/10 rounded-full px-3 py-2 text-xs focus:outline-none focus:border-[#D4AF37]/60" />
              )}
              <label className="flex items-center gap-1 text-[9px] font-bold text-white/40">
                W
                <input data-testid={`arcade-seg-weight-${i}`} value={s.w} inputMode="numeric"
                  onChange={(e) => patchSeg(i, { w: e.target.value.replace(/\D/g, "") })}
                  className="w-12 bg-[#121212] border border-white/10 rounded-full px-2 py-2 text-xs text-center focus:outline-none focus:border-[#D4AF37]/60" />
              </label>
              <span className="text-[9px] text-white/35 w-9 text-right">{totalW ? Math.round((numOr(s.w, 0) / totalW) * 100) : 0}%</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
          <p className="text-[10px] tracking-widest text-[#E25822] font-bold flex items-center gap-1.5"><Brain size={12} /> FLAVOR PEHCHAAN — DAILY QUESTION</p>
          <div className="flex flex-wrap gap-x-6">
            <Num label="REGAL COINS IF CORRECT (0-100)" testid="arcade-trivia-correct" value={cfg.trivia_mela_correct} set={field("trivia_mela_correct")} />
            <Num label="REGAL COINS LOST IF WRONG (0-100)" testid="arcade-trivia-wrong" value={cfg.trivia_mela_wrong} set={field("trivia_mela_wrong")} />
            <Num label="BONUS EVERY 7-DAY STREAK (0-500)" testid="arcade-trivia-bonus" value={cfg.trivia_streak_bonus} set={field("trivia_streak_bonus")} />
          </div>
        </div>
        <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
          <p className="text-[10px] tracking-widest text-[#D4AF37] font-bold flex items-center gap-1.5"><Brain size={12} /> FLAVOR USTAAD — WEEKLY HARD ROUND</p>
          <div className="flex flex-wrap gap-x-6">
            <Num label="REGAL COINS PER CORRECT (0-200)" testid="arcade-ustaad-mela" value={cfg.ustaad_mela_per_correct} set={field("ustaad_mela_per_correct")} />
          </div>
        </div>
        <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
          <p className="text-[10px] tracking-widest text-[#D4AF37] font-bold flex items-center gap-1.5"><Trophy size={12} /> PIND FOOD RUN — WEEKLY PRIZES</p>
          <p className="text-[10px] text-white/40 mt-1">Paid automatically every Monday. #1 wins the big Regal Coins prize + the Winner Wall; runners-up earn Regal Coins too.</p>
          <div className="flex flex-wrap items-end gap-x-6">
            <Num label="#1 REGAL COINS PRIZE (0-2000)" testid="arcade-runner-mela-first" value={cfg.runner_mela_first} set={field("runner_mela_first")} w="w-28" />
            <div className="flex gap-2">
              {cfg.runner_mela_prizes.map((p, i) => (
                <label key={i} className="text-center mt-3">
                  <span className="block text-[9px] font-black text-white/40">#{i + 2} REGAL COINS</span>
                  <input data-testid={`arcade-runner-prize-${i}`} value={p} inputMode="numeric"
                    onChange={(e) => setCfg((c) => ({ ...c, runner_mela_prizes: c.runner_mela_prizes.map((x, j) => (j === i ? e.target.value.replace(/\D/g, "") : x)) }))}
                    className="mt-1 w-20 bg-[#1A1A1A] border border-white/10 rounded-full px-2 py-2 text-sm text-center focus:outline-none focus:border-[#D4AF37]/60" />
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="rounded-xl bg-[#121212] border border-white/10 p-4">
          <p className="text-[10px] tracking-widest text-[#E25822] font-bold flex items-center gap-1.5"><Puzzle size={12} /> MASALA MATCH &nbsp;·&nbsp; <Target size={12} /> CHOORI DA CHALLA &nbsp;·&nbsp; <Dices size={12} /> SAAP-SEEDI</p>
          <div className="flex flex-wrap gap-x-6">
            <Num label="REGAL COINS PER CORRECT PAIR (0-100)" testid="arcade-masala-mela" value={cfg.masala_mela_correct} set={field("masala_mela_correct")} />
            <Num label="REGAL COINS PER RING LANDED (0-100)" testid="arcade-ringtoss-mela" value={cfg.ringtoss_mela_ring} set={field("ringtoss_mela_ring")} />
            <Num label="REGAL COINS PER MARBLE KNOCKED (0-100)" testid="arcade-kanche-mela" value={cfg.kanche_mela_marble} set={field("kanche_mela_marble")} />
            <Num label="CRICKET: REGAL COINS PER RUN (0-20)" testid="arcade-cricket-run" value={cfg.cricket_mela_per_run} set={field("cricket_mela_per_run")} />
            <Num label="CRICKET: WIN BONUS COINS (0-200)" testid="arcade-cricket-win" value={cfg.cricket_mela_win} set={field("cricket_mela_win")} />
            <Num label="SAAP-SEEDI WINNER PRIZE (0-200)" testid="arcade-saap-mela" value={cfg.saap_mela_win} set={field("saap_mela_win")} />
            <Num label="LUDO WINNER PRIZE (0-200)" testid="arcade-ludo-mela" value={cfg.ludo_mela_win} set={field("ludo_mela_win")} />
            <Num label="CARROM WINNER PRIZE (0-200)" testid="arcade-carrom-mela" value={cfg.carrom_mela_win} set={field("carrom_mela_win")} />
          </div>
        </div>
      </div>

      <MelaEvents />
    </div>
  );
};
